import VSpacingContainer from 'bloko/blocks/vSpacing/VSpacingContainer';
import { TranslatedComponent } from 'bloko/common/hooks/useTranslations';
import { formatToReactComponent } from 'bloko/common/trl';

import { GbbGoodFeature } from 'src/components/Price/Gbb';
import PeriodInDays, { PeriodVariants } from 'src/components/Price/PeriodInDays';
import { useContactsCount } from 'src/components/Price/Tariff/useContactsCount';
import translation from 'src/components/translation';
import { useIsZarplataPlatform } from 'src/hooks/usePlatform';
import { Tariff } from 'src/models/price/priceZp';
import { getResumeAccessFeatures, isUnlimitedResumeAccess } from 'src/utils/price/products';

// Для случая, когда продукт ещё не загружен
// (не выбраны какие-то поля), а фичу нужно вывести.
const DEFAULT_CIV_DURATION = 365;

const TrlKeys = {
    civDuration: 'price.tariffFeature.resumeAccess.civDuration',
    civRegion: 'price.tariffFeature.resumeAccess.civRegion.current',
    allProfRoleGroups: 'price.tariffFeature.resumeAccess.profRoleGroup.all',
};

const ResumeAccessFeatures: TranslatedComponent<{ tariff: Tariff }> = ({ tariff, trls }) => {
    const isZP = useIsZarplataPlatform();
    const hideUnlimitedAccess = isZP && isUnlimitedResumeAccess(tariff.product);
    const contactsCount = useContactsCount(tariff.product, { noUnlimited: tariff.backoffice });

    // Открытые контакты сохранятся на 365 дней
    const renderCivDurationFeature = () => {
        // Бэкофису показываем только ключевые счётные составляющие
        if (tariff.backoffice) {
            return null;
        }
        const civDuration =
            (tariff.product && getResumeAccessFeatures(tariff.product)?.civDuration) || DEFAULT_CIV_DURATION;
        return civDuration ? (
            <GbbGoodFeature>
                {formatToReactComponent(trls[TrlKeys.civDuration], {
                    '{0}': <span data-qa="civ-duration">{civDuration}</span>,
                })}
            </GbbGoodFeature>
        ) : null;
    };

    // Доступ к контактам в регионе покупки
    const renderCivRegionFeature = () => {
        // Бэкофису показываем только ключевые счётные составляющие
        return !tariff.backoffice ? <GbbGoodFeature>{trls[TrlKeys.civRegion]}</GbbGoodFeature> : null;
    };

    // Все профобласти
    const renderProfRoleGroupsFeature = () => {
        // Если есть поле выбора, в фичах не выводим.
        return tariff.fields.profRoleGroup ? null : <GbbGoodFeature>{trls[TrlKeys.allProfRoleGroups]}</GbbGoodFeature>;
    };

    // 100 резюме / Сколько угодно резюме
    const renderCivCountFeature = () => {
        if (hideUnlimitedAccess) {
            return null;
        }
        // Если есть поле выбора, в фичах не выводим.
        return !tariff.fields.civCount && contactsCount ? <GbbGoodFeature>{contactsCount}</GbbGoodFeature> : null;
    };

    // Срок доступа — 30 дней
    const renderPeriodFeature = () => {
        // Если есть поле выбора, в фичах не выводим.
        return !tariff.fields.period && tariff.product?.period ? (
            <GbbGoodFeature>
                <PeriodInDays period={tariff.product.period} variant={PeriodVariants.ContactsDetailed} />
            </GbbGoodFeature>
        ) : null;
    };

    return (
        <VSpacingContainer base={4}>
            {renderCivDurationFeature()}
            {renderCivRegionFeature()}
            {renderProfRoleGroupsFeature()}
            {renderCivCountFeature()}
            {renderPeriodFeature()}
        </VSpacingContainer>
    );
};

export default translation(ResumeAccessFeatures);
